import { useCallback, useEffect, useState } from "react";

/**
 * Anonymous-first XP and rank ladder.
 *
 * Everything lives in localStorage so a first-time visitor gets the full
 * progression loop with zero friction; the account prompt only appears once
 * there is real progress worth saving.
 */
const KEY = "sa.xp.v1";

export type Store = {
  xp: number;
  /** discovery slugs the reader has certified, with their score out of 2 */
  certified: Record<string, number>;
  /** quiz slugs already rewarded, so replaying cannot farm XP */
  quizzes: Record<string, number>;
  /** true once the reader dismissed the "save your progress" nudge */
  savePromptDismissed: boolean;
  /** prevents importing the former quiz-only XP counter more than once */
  legacyQuizXpImported: boolean;
};

const empty: Store = { xp: 0, certified: {}, quizzes: {}, savePromptDismissed: false, legacyQuizXpImported: false };

export const RANKS = [
  { name: "Scroller", min: 0 },
  { name: "Lab Analyst", min: 201 },
  { name: "Field Researcher", min: 601 },
  { name: "Apex Smartasaurus", min: 1501 },
] as const;

/** A discovery is only certified on a clean sweep - that's what makes it mean something. */
export const XP_PER_CERTIFY = 50;


export function rankFor(xp: number) {
  let index = 0;
  for (let i = 0; i < RANKS.length; i++) if (xp >= RANKS[i]!.min) index = i;
  const current = RANKS[index]!;
  const next = RANKS[index + 1] ?? null;
  const span = next ? next.min - current.min : 1;
  const progress = next ? Math.min(1, (xp - current.min) / span) : 1;
  return { index, current, next, progress, toNext: next ? Math.max(0, next.min - xp) : 0 };
}

function read(): Store {
  if (typeof window === "undefined") return empty;
  try {
    const raw = window.localStorage.getItem(KEY);
    const current = raw ? { ...empty, ...(JSON.parse(raw) as Partial<Store>) } : { ...empty };
    // Quiz XP used to live in a disconnected counter. Preserve what returning
    // visitors already earned, once, when upgrading to the shared progression.
    if (!current.legacyQuizXpImported) {
      const legacy = JSON.parse(window.localStorage.getItem("sa.quiz.score.v1") ?? "null") as { xp?: unknown } | null;
      const earned = Number(legacy?.xp ?? 0);
      if (Number.isFinite(earned) && earned > 0) current.xp += Math.floor(earned);
      current.legacyQuizXpImported = true;
      window.localStorage.setItem(KEY, JSON.stringify(current));
    }
    return current;
  } catch {
    return empty;
  }
}

/** Every mounted useUserXp() instance (header chip, article widgets) listens for this. */
const XP_EVENT = "sa:xp-changed";

function write(s: Store) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* quota */
  }
  try {
    window.dispatchEvent(new CustomEvent<Store>(XP_EVENT, { detail: s }));
  } catch {
    /* no-op */
  }
}

export function useUserXp(slug?: string) {
  const [store, setStore] = useState<Store>(empty);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setStore(read());
    setHydrated(true);
    // Live-sync across components and browser tabs so the rank chip updates
    // the instant XP is earned - no refresh.
    const onLocal = (e: Event) => {
      const detail = (e as CustomEvent<Store>).detail;
      setStore(detail ?? read());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setStore(read());
    };
    window.addEventListener(XP_EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(XP_EVENT, onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const certifiedHere = slug ? store.certified[slug] : undefined;
  const certifiedCount = Object.keys(store.certified).length;
  const quizCount = Object.keys(store.quizzes).length;

  /**
   * Records a clean sweep. Anything short of perfect isn't certified - the
   * reader simply retries - so XP is awarded once, per discovery, on success.
   */
  const certify = useCallback(
    (correct: number, outOf: number) => {
      if (!slug || correct < outOf) return 0;
      // Always start from the freshest saved state so awards from other
      // widgets on the page are never overwritten by a stale copy.
      const fresh = read();
      if (fresh.certified[slug] !== undefined) return 0;
      const next: Store = {
        ...fresh,
        xp: fresh.xp + XP_PER_CERTIFY,
        certified: { ...fresh.certified, [slug]: correct },
      };
      write(next);
      setStore(next);
      return XP_PER_CERTIFY;
    },
    [slug],
  );

  const awardQuiz = useCallback(
    (quizSlug: string, points: number) => {
      const fresh = read();
      if (!quizSlug || points <= 0 || fresh.quizzes[quizSlug] !== undefined) return 0;
      const next: Store = {
        ...fresh,
        xp: fresh.xp + points,
        quizzes: { ...fresh.quizzes, [quizSlug]: points },
      };
      write(next);
      setStore(next);
      return points;
    },
    [],
  );



  const dismissSavePrompt = useCallback(() => {
    setStore((prev) => {
      const next = { ...prev, savePromptDismissed: true };
      write(next);
      return next;
    });
  }, []);

  return {
    ...store,
    hydrated,
    certifiedHere,
    certifiedCount,
    quizCount,
    rank: rankFor(store.xp),
    certify,
    awardQuiz,
    dismissSavePrompt,
  };
}
